// src/pages/Onboarding.jsx
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { createClient } from '../api/kailux'
import { PartyPopper, Users, Upload, Rocket, BarChart3, Landmark, Link2 } from 'lucide-react'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
const MONTHS = ['January','February','March','April','May','June',
  'July','August','September','October','November','December']

export default function Onboarding() {
  const [step, setStep]       = useState(0)
  const [loading, setLoading] = useState(false)
  const [client, setClient]   = useState({ name:'', yearEnd:'February', method:'' })
  const { firm }              = useAuth()
  const navigate              = useNavigate()

  const steps = [
    { icon:PartyPopper, title:`Welcome to KaiLux!`, sub:'Your account is ready. Let\'s get your first client set up in 2 minutes.' },
    { icon:Users, title:'Add your first client', sub:'Tell us about the first business whose AFS you want to prepare.' },
    { icon:Upload, title:'How will you start?', sub:'KaiLux works with whatever data your client has.' },
    { icon:Rocket, title:'You\'re ready!', sub:'Everything is set up. Let\'s produce your first AFS.' },
  ]

  const handleFinish = async () => {
    setLoading(true)
    try {
      if (client.name && firm?.id) {
        await createClient({
          firm_id: firm.id,
          name: client.name,
          year_end_month: client.yearEnd,
        })
      }
    } catch(e) { console.error(e) }
    navigate('/dashboard')
  }

  const current = steps[step]

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif",
      color:T.tx, display:'flex', alignItems:'center', justifyContent:'center', padding:'24px' }}>

      <div style={{ position:'fixed', top:'25%', left:'50%', transform:'translateX(-50%)',
        width:'600px', height:'300px', pointerEvents:'none',
        background:`radial-gradient(ellipse,${alpha(T.or, 0.07)} 0%,transparent 70%)` }}/>

      <div style={{ width:'100%', maxWidth:'520px', position:'relative', zIndex:1 }}>

        {/* Progress dots */}
        <div style={{ display:'flex', justifyContent:'center', gap:'8px', marginBottom:'32px' }}>
          {steps.map((_,i)=>(
            <div key={i} style={{ height:'8px', borderRadius:'999px',
              width:i===step?24:8, transition:'all 0.3s',
              background:i<step?T.gn:i===step?T.or:T.bd }}/>
          ))}
        </div>

        <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'20px',
          padding:'48px 40px', textAlign:'center',
          boxShadow:T.shl }}>

          <current.icon size={44} strokeWidth={1.5} color={T.or} style={{ marginBottom:'20px' }} />
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'24px',
            fontWeight:'800', letterSpacing:'-0.5px', marginBottom:'8px' }}>
            {current.title}
          </div>
          <div style={{ fontSize:'14px', color:T.mt, lineHeight:'1.7',
            marginBottom:'28px', maxWidth:'380px', margin:'0 auto 28px' }}>
            {current.sub}
          </div>

          {/* Step 1 — client form */}
          {step===1 && (
            <div style={{ textAlign:'left', marginBottom:'20px' }}>
              <div style={{ marginBottom:'14px' }}>
                <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                  color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
                  CLIENT COMPANY NAME
                </label>
                <input value={client.name} onChange={e=>setClient(c=>({...c,name:e.target.value}))}
                  placeholder="e.g. Mvelo Trading (Pty) Ltd"
                  style={{ width:'100%', padding:'12px 14px', background:T.s3,
                    border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                    fontSize:'14px', fontFamily:"'Outfit',sans-serif",
                    outline:'none', boxSizing:'border-box' }}
                  onFocus={e=>e.target.style.borderColor=T.or}
                  onBlur={e=>e.target.style.borderColor=T.bd}/>
              </div>
              <div>
                <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                  color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
                  FINANCIAL YEAR END
                </label>
                <select value={client.yearEnd}
                  onChange={e=>setClient(c=>({...c,yearEnd:e.target.value}))}
                  style={{ width:'100%', padding:'12px 14px', background:T.s3,
                    border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                    fontSize:'14px', fontFamily:"'Outfit',sans-serif", outline:'none' }}>
                  {MONTHS.map(m=><option key={m} value={m} style={{background:T.s2}}>{m}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Step 2 — method */}
          {step===2 && (
            <div style={{ textAlign:'left', marginBottom:'20px' }}>
              {[
                { id:'trial_balance', icon:BarChart3, title:'Trial Balance', desc:'Excel or CSV from QuickBooks, Xero, Sage or manual' },
                { id:'bank_statement', icon:Landmark, title:'Bank Statement', desc:'PDF or Excel from FNB, ABSA, Nedbank, Standard Bank, Capitec' },
                { id:'both', icon:Link2, title:'Both', desc:'Start with bank statements, add trial balance for reconciliation' },
              ].map(m=>(
                <label key={m.id} style={{ display:'flex', gap:'12px', padding:'14px 16px',
                  borderRadius:'12px', cursor:'pointer', marginBottom:'8px',
                  background:client.method===m.id?T.ord:T.s2,
                  border:`1.5px solid ${client.method===m.id?T.or:T.bd}`,
                  transition:'all 0.2s' }}>
                  <input type="radio" name="method" value={m.id}
                    checked={client.method===m.id}
                    onChange={()=>setClient(c=>({...c,method:m.id}))}
                    style={{ accentColor:T.or, marginTop:'2px' }}/>
                  <m.icon size={20} strokeWidth={2} />
                  <div>
                    <div style={{ fontSize:'14px', fontWeight:'700',
                      color:client.method===m.id?T.or:T.tx, marginBottom:'3px' }}>{m.title}</div>
                    <div style={{ fontSize:'12px', color:T.mt }}>{m.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Step 3 — checklist */}
          {step===3 && (
            <div style={{ textAlign:'left', marginBottom:'24px' }}>
              {[
                { done:true,  text:'Firm account created' },
                { done:true,  text:client.name?`Client "${client.name}" added`:'First client added' },
                { done:true,  text:'Engagement type configured' },
                { done:false, text:'Upload your first trial balance or bank statement' },
                { done:false, text:'Run AI mapping and generate your first AFS' },
              ].map((item,i)=>(
                <div key={i} style={{ display:'flex', gap:'12px', alignItems:'center',
                  padding:'10px 0', borderBottom:`1px solid ${T.bd}` }}>
                  <div style={{ width:'20px', height:'20px', borderRadius:'50%', flexShrink:0,
                    background:item.done?T.gnd:T.s2,
                    border:`1.5px solid ${item.done?T.gn:T.bd}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:'10px', color:T.gn }}>
                    {item.done?<Icon name="check" />:''}
                  </div>
                  <span style={{ fontSize:'14px', color:item.done?T.tx:T.mt }}>{item.text}</span>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={step===steps.length-1?handleFinish:()=>setStep(s=>s+1)}
            disabled={loading}
            style={{ width:'100%', padding:'12px', borderRadius:'12px', border:'none',
              background:T.prg,
              color:T.onpri, fontFamily:"'Outfit',sans-serif",
              fontSize:'15px', fontWeight:'700', cursor:'pointer',
              boxShadow:`0 4px 20px ${alpha(T.pri, 0.35)}`,
              display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
            {loading?(
              <><span style={{ width:'14px',height:'14px',border:`2px solid ${alpha(T.onpri, 0.3)}`,
                borderTopColor:T.onpri,borderRadius:'50%',
                animation:'spin 0.8s linear infinite',display:'inline-block' }}/>
              Opening dashboard...</>
            ):step===steps.length-1?'Open Dashboard →':step===0?'Let\'s go →':'Continue →'}
          </button>

          {step>0 && step<steps.length-1 && (
            <button onClick={()=>setStep(s=>s+1)}
              style={{ marginTop:'12px', background:'none', border:'none',
                color:T.mt, fontFamily:"'Outfit',sans-serif", fontSize:'13px',
                cursor:'pointer', textDecoration:'underline' }}>
              Skip for now
            </button>
          )}
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
